<template>
  <section class="h-full mx-4" id="myDataDocumentsView">
    <BaseTable
      v-if="myDataDocuments"
      :headers="myDataDocumentsHeaders"
      :list="myDataDocuments"
      :total-records="myDataDocumentsTotalCount"
      table-id="myDataDocumentsTable"
      multi-select
      hide-multiselect-delete
      is-add-new-hidden
      @refresh-data="onRefreshData"
      @apply-filters="onApplyFilters"
      @reset-filters="onResetFilters"
      @emit-selected-items="uploadSelectedToESA">
      <template #extraButtonContent>
        <BaseButton color="primary" size="sm">
          <Icon icon="material-symbols:upload" class="w-4 h-4 mr-1" />
          {{ $t('myDataDocuments.uploadSelected') }}
        </BaseButton>
      </template>
      <template #filters>
        <div class="grid grid-cols-3 gap-8 mx-10 my-5 pb-8">
          <!-- First Column: Document Types, Series, and Division -->
          <div class="space-y-4">
            <div class="space-y-1">
              <label class="text-sm tracking-wide text-gray-400 uppercase">{{
                $t('myDataDocuments.filters.byDocumentType')
              }}</label>
              <BaseMultiSelectDropdown
                v-model="filterDocumentTypes"
                :listItems="dropDownDocumentTypes" />
            </div>
            <div class="space-y-1">
              <label class="text-sm tracking-wide text-gray-400 uppercase">{{
                $t('myDataDocuments.filters.byDocumentSeries')
              }}</label>
              <BaseMultiSelectDropdown
                v-model="filterDocumentSeries"
                :listItems="dropDownDocumentSeries" />
            </div>
            <div class="space-y-1">
              <label class="text-sm tracking-wide text-gray-400 uppercase">{{
                $t('myDataDocuments.filters.byDivision')
              }}</label>
              <BaseMultiSelectDropdown v-model="filterDivisions" :listItems="dropDownDivisions" />
            </div>
          </div>

          <!-- Second Column: Date Filters -->
          <div class="space-y-4">
            <div class="space-y-1">
              <label class="text-sm tracking-wide text-gray-400 uppercase">{{
                $t('myDataDocuments.filters.dateFrom')
              }}</label>
              <DateTimePicker
                ref="filterDateFromPickerRef"
                v-model="filterDateStringFrom"
                :show-time-picker="false"
                start-empty
                show-error-as-tooltip />
            </div>
            <div class="space-y-1">
              <label class="text-sm tracking-wide text-gray-400 uppercase">{{
                $t('myDataDocuments.filters.dateTo')
              }}</label>
              <DateTimePicker
                ref="filterDateToPickerRef"
                v-model="filterDateStringTo"
                :show-time-picker="false"
                start-empty
                show-error-as-tooltip />
            </div>
          </div>

          <!-- Third Column: Sync Status -->
          <div class="space-y-4">
            <div class="space-y-2">
              <label class="text-sm tracking-wide text-gray-400 uppercase">{{
                $t('myDataDocuments.filters.bySyncStatus')
              }}</label>
              <div class="space-y-1">
                <div class="flex items-center gap-2">
                  <BaseCheckbox
                    v-model="filterSyncSuccess"
                    @update:model-value="onSyncSuccessFilterChange" />
                  <span class="text-sm text-gray-700">{{
                    $t('myDataDocuments.filters.syncSuccess')
                  }}</span>
                </div>
                <div class="flex items-center gap-2">
                  <BaseCheckbox
                    v-model="filterSyncFailed"
                    @update:model-value="onSyncFailedFilterChange" />
                  <span class="text-sm text-gray-700">{{
                    $t('myDataDocuments.filters.syncFailed')
                  }}</span>
                </div>
                <div class="flex items-center gap-2">
                  <BaseCheckbox
                    v-model="filterSyncUnsynced"
                    @update:model-value="onSyncUnsyncedFilterChange" />
                  <span class="text-sm text-gray-700">{{
                    $t('myDataDocuments.filters.syncUnsynced')
                  }}</span>
                </div>
              </div>
            </div>
            <div>
              <label class="space-y-2 text-sm tracking-wide text-gray-400 uppercase">{{
                $t('material.isActive')
              }}</label>
              <BaseTriStateToggle
                :label="$t('baseObject.isActive')"
                name="isActive"
                v-model="filterIsActive" />
            </div>
          </div>
        </div>
      </template>

      <template #custom="{ item }: any">
        <div class="text-center">
          <button
            :class="getSyncStatusClasses(item)"
            class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium hover:opacity-80 hover:scale-105 hover:shadow-md transition-all duration-200 cursor-pointer"
            @click="handleSyncStatusClick(item)">
            <span
              :class="getSyncStatusDotClass(item)"
              class="w-1.5 h-1.5 mr-1.5 rounded-full"></span>
            {{ getSyncStatusText(item) }}
          </button>
        </div>
      </template>
    </BaseTable>

    <!-- Error Modal -->
    <BaseModalConfirm
      v-if="errorModalActive"
      @cancel="errorModalActive = false"
      @confirm="uploadToESA(selectedDocument)">
      <template #title>{{ $t('myDataDocuments.errorDetails') }}</template>
      <template #text>
        <div class="whitespace-pre-wrap">{{ selectedErrorMessage }}</div>
      </template>
      <template #buttonText>{{ $t('myDataDocuments.uploadSelected') }}</template>
    </BaseModalConfirm>

    <!-- Properties Modal -->
    <BaseModalConfirm
      v-if="propertiesModalActive"
      @cancel="propertiesModalActive = false"
      @confirm="uploadToESA(selectedDocument)">
      <template #title>{{ $t('myDataDocuments.propertiesDetails') }}</template>
      <template #text>
        <div v-if="selectedDocument" class="space-y-3">
          <div class="grid grid-cols-2 gap-4">
            <div>
              <label class="text-sm font-medium text-gray-500"
                >{{ $t('myDataDocuments.myDataMark') }}:</label
              >
              <p class="text-sm text-gray-900">{{ selectedDocument.myDataMark || '-' }}</p>
            </div>
            <div>
              <label class="text-sm font-medium text-gray-500"
                >{{ $t('myDataDocuments.myDataUuid') }}:</label
              >
              <p class="text-sm text-gray-900 break-all">
                {{ selectedDocument.myDataUuid || '-' }}
              </p>
            </div>
            <div>
              <label class="text-sm font-medium text-gray-500"
                >{{ $t('myDataDocuments.myDataQrCode') }}:</label
              >
              <p class="text-sm text-gray-900 break-all">
                {{ selectedDocument.myDataQrCode || '-' }}
              </p>
            </div>
          </div>
        </div>
      </template>
      <template #buttonText>{{ $t('myDataDocuments.uploadSelected') }}</template>
    </BaseModalConfirm>
  </section>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { getMyDataDocuments, uploadDocumentsToESA } from '@/services/api/documents';
import type { MyDataDocumentHeader, MyDataDocumentsFiltersData } from '@/types/Documents';
import type { SelectDropdownItem } from '@/types/Base';
import { isoDateToFullDate } from '@/utils/date';
import { useLookupsStore } from '@/stores/lookups';
import { storeToRefs } from 'pinia';
import BaseTable from '@/components/common/BaseTable.vue';
import BaseCheckbox from '@/components/common/BaseCheckbox.vue';
import BaseMultiSelectDropdown from '@/components/common/BaseMultiSelectDropdown.vue';
import DateTimePicker from '@/components/common/DateTimePicker.vue';
import BaseModalConfirm from '@/components/common/BaseModalConfirm.vue';
import { useToastStore } from '@/stores/toast';
import BaseTriStateToggle from '@/components/common/BaseTriStateToggle.vue';

const { t: $t } = useI18n({ useScope: 'global' });
const toastStore = useToastStore();
const lookupsStore = useLookupsStore();
const { documentTypes, documentSeries, divisions } = storeToRefs(lookupsStore);
const filterDateFromPickerRef = ref();
const filterDateToPickerRef = ref();
const myDataDocuments = ref<MyDataDocumentHeader[]>([]);
const myDataDocumentsTotalCount = ref<number>(0);
const filterIsActive = ref<boolean | undefined>(undefined);

const myDataDocumentsHeaders = ref([
  { value: 'officialDate', text: $t('date.date'), type: 'text', active: true },
  {
    value: 'divisionName',
    text: $t('myDataDocuments.division'),
    type: 'text',
    active: true,
  },
  {
    value: 'documentTypeName',
    text: $t('myDataDocuments.documentType'),
    type: 'text',
    active: true,
  },
  {
    value: 'documentSeriesName',
    text: $t('myDataDocuments.documentSeries'),
    type: 'text',
    active: true,
  },
  {
    value: 'documentNumber',
    text: $t('myDataDocuments.documentNumber'),
    type: 'text',
    active: true,
  },
  { value: 'traderName', text: $t('myDataDocuments.trader'), type: 'text', active: true },
  { value: 'traderSiteName', text: $t('myDataDocuments.traderSite'), type: 'text', active: true },
  {
    value: 'totalGross',
    text: $t('myDataDocuments.totalGross'),
    type: 'number',
    active: true,
    price: true,
  },
  {
    value: 'totalQuantity',
    text: $t('myDataDocuments.totalQuantity'),
    type: 'number',
    active: true,
  },
  { value: 'myDataMark', text: $t('myDataDocuments.myDataMark'), type: 'number', active: true },
  {
    value: 'myDataSyncSuccess',
    text: $t('myDataDocuments.syncStatus'),
    type: 'custom',
    active: true,
  },
]);

const filterDateStringFrom = ref<string | undefined>(undefined);
const filterDateStringTo = ref<string | undefined>(undefined);
const filterDocumentTypes = ref<SelectDropdownItem[]>([]);
const filterDocumentSeries = ref<SelectDropdownItem[]>([]);
const filterDivisions = ref<SelectDropdownItem[]>([]);
const filterSyncSuccess = ref<boolean>(false);
const filterSyncFailed = ref<boolean>(false);
const filterSyncUnsynced = ref<boolean>(false);

const dropDownDocumentTypes = computed(() =>
  documentTypes.value
    .filter((type) => type.syncWithMyData)
    .map((type) => ({
      value: type.id,
      name: type.name || '',
    }))
);

const dropDownDocumentSeries = computed(() =>
  documentSeries.value.map((series: any) => ({
    value: series.id,
    name: series.name || '',
  }))
);

const dropDownDivisions = computed(() =>
  divisions.value.map((division) => ({
    value: division.id,
    name: division.name || '',
  }))
);

const errorModalActive = ref(false);
const selectedErrorMessage = ref<string>('');
const propertiesModalActive = ref(false);
const selectedDocument = ref<MyDataDocumentHeader>();

onMounted(async () => {
  await Promise.all([
    lookupsStore.getDocumentTypesLookup(),
    lookupsStore.getDocumentSeriesLookup(),
    lookupsStore.getDivisionsLookup(),
  ]);
  onRefreshData({ page: 1, itemsPerPage: 50, sortColumn: 'officialDate', sortAscending: false });
});

function onSyncSuccessFilterChange(value: boolean | undefined) {
  if (value) {
    filterSyncFailed.value = false;
    filterSyncUnsynced.value = false;
  }
}

function onSyncFailedFilterChange(value: boolean | undefined) {
  if (value) {
    filterSyncSuccess.value = false;
    filterSyncUnsynced.value = false;
  }
}

function onSyncUnsyncedFilterChange(value: boolean | undefined) {
  if (value) {
    filterSyncSuccess.value = false;
    filterSyncFailed.value = false;
  }
}

async function onRefreshData(data: MyDataDocumentsFiltersData) {
  const sortColumn =
    data.column !== undefined ? myDataDocumentsHeaders.value[data.column]?.value : 'officialDate';
  const sortAscending = data.order === 'asc' || data.order === undefined;

  const params = {
    page: data.page || 1,
    itemsPerPage: data.itemsPerPage || 50,
    query: data.query || '',
    sortColumn: sortColumn,
    sortAscending: sortAscending,
    dateFrom: data.dateFrom,
    dateTo: data.dateTo,
    divisionIds: data.divisionIds,
    documentSeriesIds: data.documentSeriesIds,
    documentTypeIds: data.documentTypeIds,
    syncedSuccess: data.syncedSuccess,
    syncedFailure: data.syncedFailure,
    unsynced: data.unsynced,
    isActive: data.isActive,
  };

  const res = await getMyDataDocuments(params);
  if (res) {
    myDataDocuments.value = res.data.map((item) => ({
      ...item,
      officialDate: isoDateToFullDate(item.officialDate) || '',
    }));
    myDataDocumentsTotalCount.value = res.count;
  }
}

async function onApplyFilters(data: MyDataDocumentsFiltersData) {
  const fromDateValid = filterDateFromPickerRef.value?.isValid ?? true;
  const toDateValid = filterDateToPickerRef.value?.isValid ?? true;

  if (!fromDateValid || !toDateValid) {
    toastStore.error({ text: $t('messages.error.dateError') });
    return;
  }

  data.dateFrom = filterDateStringFrom.value || undefined;
  data.dateTo = filterDateStringTo.value || undefined;

  data.syncedSuccess = filterSyncSuccess.value;
  data.syncedFailure = filterSyncFailed.value;
  data.unsynced = filterSyncUnsynced.value;

  data.documentTypeIds = filterDocumentTypes.value
    .map((item) => item.value)
    .filter((value): value is string => typeof value === 'string');
  data.documentSeriesIds = filterDocumentSeries.value
    .map((item) => item.value)
    .filter((value): value is string => typeof value === 'string');
  data.divisionIds = filterDivisions.value
    .map((item) => item.value)
    .filter((value): value is string => typeof value === 'string');

  data.isActive = filterIsActive.value;
  await onRefreshData(data);
}

function onResetFilters() {
  filterDateStringFrom.value = undefined;
  filterDateStringTo.value = undefined;
  filterDocumentTypes.value = [];
  filterDivisions.value = [];
  filterDocumentSeries.value = [];
  filterSyncSuccess.value = false;
  filterSyncFailed.value = false;
  filterSyncUnsynced.value = false;
  filterIsActive.value = undefined;

  onRefreshData({
    itemsPerPage: 50,
    sortColumn: 'officialDate',
    sortAscending: false,
    page: 1,
  });
}

function getSyncStatusText(doc: MyDataDocumentHeader): string {
  if (doc.myDataSyncSuccess === true) return $t('myDataDocuments.syncSuccess');
  if (doc.myDataSyncSuccess === false && (!!doc.myDataErrorMessage || !!doc.myDataMark))
    return $t('myDataDocuments.syncFailed');
  return $t('myDataDocuments.syncUnsynced');
}

function getSyncStatusDotClass(doc: MyDataDocumentHeader): string {
  if (doc.myDataSyncSuccess === true) return 'bg-green-400';
  if (doc.myDataSyncSuccess === false && (!!doc.myDataErrorMessage || !!doc.myDataMark))
    return 'bg-red-400';
  return 'bg-gray-400';
}

function getSyncStatusClasses(doc: MyDataDocumentHeader): string {
  if (doc.myDataSyncSuccess === true) return 'bg-green-100 text-green-800';
  if (doc.myDataSyncSuccess === false && (!!doc.myDataErrorMessage || !!doc.myDataMark))
    return 'bg-red-100 text-red-800';
  return 'bg-gray-100 text-gray-800';
}

function handleSyncStatusClick(item: MyDataDocumentHeader) {
  selectedDocument.value = item;
  if (item.myDataSyncSuccess === true) {
    propertiesModalActive.value = true;
  } else if (item.myDataSyncSuccess === false && (!!item.myDataErrorMessage || !!item.myDataMark)) {
    selectedErrorMessage.value = item.myDataErrorMessage || $t('myDataDocuments.noErrorMessage');
    errorModalActive.value = true;
  } else uploadToESA(item);
}

function uploadToESA(item: MyDataDocumentHeader | undefined) {
  if (errorModalActive.value) errorModalActive.value = false;
  if (propertiesModalActive.value) propertiesModalActive.value = false;
  if (!item) return;
  uploadSelectedToESA([item]);
}

async function uploadSelectedToESA(items: MyDataDocumentHeader[]) {
  await uploadDocumentsToESA(items.map((item) => item.id));
  onRefreshData({ page: 1, itemsPerPage: 50, sortColumn: 'officialDate', sortAscending: false });
}
</script>
